import { useRef } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PenLine, Printer, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, fmtTime } from "@/lib/api";
import { BigButton } from "@/components/BigButton";
import { t, t as tr } from "@/i18n";

const F = ({ n, label, children }) => (
  <div className="border border-slate-300 p-2 min-h-[64px]">
    <p className="text-[10px] uppercase tracking-wider text-slate-500">{n ? `${n}. ` : ""}{tr(label)}</p>
    <div className="text-sm text-slate-900 font-medium whitespace-pre-line">{children}</div>
  </div>
);

const DdtBody = ({ t, d }) => (
  <>
    <div className="grid grid-cols-2 gap-0">
      <F label="Mittente / Luogo di carico">{`${t.pickup.name}\n${t.pickup.address}`}</F>
      <F label="Destinatario / Luogo di destinazione">{`${t.delivery.name}\n${t.delivery.address}`}</F>
      <F label="Vettore">{t.carrier.name}</F>
      <F label="Causale del trasporto">{tr("Vendita")}</F>
      <F label="Data e ora inizio trasporto">{fmtTime(t.pickup.time)}</F>
      <F label="Mezzo / Targa">{`${tr(t.vehicle.type)} · ${t.vehicle.plate}`}</F>
    </div>
    <table className="w-full mt-3 text-sm text-slate-900 border border-slate-300">
      <thead className="bg-slate-100 text-xs uppercase"><tr><th className="p-2 text-left">{tr("Descrizione beni")}</th><th className="p-2">{tr("Colli")}</th><th className="p-2">{tr("Peso")}</th></tr></thead>
      <tbody><tr><td className="p-2">{tr(t.goods.description)}</td><td className="p-2 text-center">{t.goods.packages}</td><td className="p-2 text-center">{t.goods.weight_kg} kg</td></tr></tbody>
    </table>
    <F label="Annotazioni">{tr(t.goods.notes)}</F>
  </>
);

const CmrBody = ({ t }) => (
  <div className="grid grid-cols-2 gap-0">
    <F n="1" label="Mittente">{`${t.pickup.name}\n${t.pickup.address}`}</F>
    <F n="16" label="Vettore">{t.carrier.name}</F>
    <F n="2" label="Destinatario">{`${t.delivery.name}\n${t.delivery.address}`}</F>
    <F n="3" label="Luogo previsto per la consegna">{t.delivery.address}</F>
    <F n="4" label="Luogo e data della presa in carico">{`${t.pickup.address}\n${fmtTime(t.pickup.time)}`}</F>
    <F n="5" label="Documenti allegati">{`DDT ${t.code}`}</F>
    <F n="7-9" label="Numero colli · Natura della merce">{`${t.goods.packages} · ${tr(t.goods.description)}`}</F>
    <F n="11" label="Peso lordo kg">{t.goods.weight_kg}</F>
    <F n="13" label="Istruzioni del mittente">{tr(t.goods.notes)}</F>
    <F n="25" label="Targa mezzo">{t.vehicle.plate}</F>
  </div>
);

export const DocumentViewer = ({ trip, type, onClose, onSigned }) => {
  const ref = useRef(null);
  const doc = trip?.documents.find((d) => d.type === type);
  const sign = async () => {
    try {
      const { data } = await api.post(`/trips/${trip.id}/documents/${type}/sign`);
      toast.success(t("{type} firmato digitalmente", { type }));
      onSigned?.(data);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const print = () => {
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>${doc.number}</title><script src="https://cdn.tailwindcss.com"></script></head><body class="p-6">${ref.current.innerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 800);
  };
  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex flex-col gap-0 p-0 w-screen max-w-none h-[100dvh] rounded-none sm:w-[calc(100vw-2rem)] sm:max-w-3xl sm:h-auto sm:max-h-[92vh] sm:rounded-2xl bg-[#0b0f17] border-[#243047] text-white overflow-hidden [&>button:last-child]:hidden" data-testid="document-viewer">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-[#243047] shrink-0">
        <DialogTitle className="font-display text-2xl sm:text-3xl uppercase tracking-wide leading-tight">{type === "DDT" ? t("Documento di trasporto (DDT)") : t("Lettera di vettura CMR")}</DialogTitle>
          <button data-testid="doc-close-btn" onClick={onClose} className="nv-press shrink-0 min-h-[56px] px-4 rounded-xl bg-[#ef0298] nv-on-accent font-display text-lg font-bold uppercase flex items-center gap-2">
            <X className="h-6 w-6" /> {t("Chiudi")}
          </button>
        </div>
        {doc && (
          <>
            <div className="flex-1 overflow-y-auto p-4">
            <div ref={ref} className="bg-white rounded-xl p-4 sm:p-5 text-slate-900 min-w-0" data-testid="document-paper">
              <div className="flex justify-between items-start border-b-2 border-slate-900 pb-3 mb-3">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500">{t(type === "CMR" ? "Lettera di vettura internazionale" : "Documento di trasporto")}</p>
                  <p className="text-2xl font-black">{doc.number}</p>
                </div>
                <p className="text-sm text-right">{t("Emesso il")}<br /><b>{fmtTime(doc.issued_at)}</b></p>
              </div>
              {type === "DDT" ? <DdtBody t={trip} d={doc} /> : <CmrBody t={trip} />}
              <div className="mt-3 border border-slate-300 p-3 text-sm">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">{t("Firma del conducente")}</p>
                {doc.signed_at ? <p className="font-semibold text-emerald-700">{t("Firmato da {n} il {t}", { n: doc.signed_by, t: fmtTime(doc.signed_at) })}</p> : <p className="text-slate-400">{t("In attesa di firma")}</p>}
              </div>
              <p className="text-[10px] text-slate-400 mt-2">{t("Documento digitale con dati dimostrativi")}</p>
            </div>
            </div>
            <div className="grid grid-cols-2 gap-3 p-4 border-t border-[#243047] shrink-0 pb-[max(1rem,env(safe-area-inset-bottom))]">
              {doc.signed_at ? (
                <BigButton testId="doc-signed-indicator" icon={CheckCircle2} color="#10b981" variant="outline" label={t("Firmato")} sub={fmtTime(doc.signed_at)} subClassName="hidden sm:block" disabled />
              ) : (
                <BigButton testId="doc-sign-btn" icon={PenLine} color="#ef0298" label={t("Firma presa visione")} sub={t("Conferma di aver ricevuto il documento")} subClassName="hidden sm:block" onClick={sign} />
              )}
              <BigButton testId="doc-print-btn" icon={Printer} color="#4accff" variant="outline" label={t("Stampa / PDF")} sub={t("Apri per stampare o salvare")} subClassName="hidden sm:block" onClick={print} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
